import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

type AdminRole = 'super_admin' | 'admin' | 'moderator' | 'data_entry' | 'viewer';

interface Admin {
  id: number;
  name: string;
  email: string;
  role: AdminRole;
  token: string;
  lastActivity?: number;
}

interface AdminAuthContextType {
  admin: Admin | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => void;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isDataEntry: boolean;
  refreshActivity: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const ADMIN_STORAGE_KEY = 'patakare_admin_auth';
const LOGIN_ATTEMPTS_KEY = 'patakare_admin_login_attempts';
const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000;

const isTokenValid = (token: string): boolean => {
  if (!token) return false;
  try {
    const parts = token.split('.');
    // Backend issues HMAC-signed "payload.signature" tokens (2 parts) — payload is parts[0].
    // Fall back to parts[1] for legacy 3-part JWT-style tokens, or the whole string otherwise.
    const payloadStr = parts.length === 2 ? parts[0] : parts.length === 3 ? parts[1] : token;
    const b64 = payloadStr.replace(/-/g, '+').replace(/_/g, '/');
    const tokenData = JSON.parse(atob(b64));
    const expSec = tokenData.exp ?? tokenData.expires;
    if (expSec && expSec * 1000 > Date.now()) return true;
    return false;
  } catch {
    return false;
  }
};

const getLoginAttempts = (): { count: number; lockUntil: number } => {
  try {
    const stored = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return { count: 0, lockUntil: 0 };
};

const setLoginAttempts = (data: { count: number; lockUntil: number }) => {
  localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(data));
};

const resetLoginAttempts = () => {
  localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
};

const getApiUrl = (): string => {
  return (import.meta as any).env?.VITE_API_URL || '/bridgeapi';
};

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback(() => {
    setAdmin(null);
    localStorage.removeItem(ADMIN_STORAGE_KEY);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      clearSession();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearSession]);

  const refreshActivity = useCallback(() => {
    if (admin) {
      const updated = { ...admin, lastActivity: Date.now() };
      setAdmin(updated);
      localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(updated));
      resetInactivityTimer();
    }
  }, [admin, resetInactivityTimer]);

  useEffect(() => {
    const restore = async () => {
      const stored = localStorage.getItem(ADMIN_STORAGE_KEY);
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const parsed: Admin = JSON.parse(stored);
        if (!isTokenValid(parsed.token)) {
          clearSession();
          setLoading(false);
          return;
        }
        const lastActivity = parsed.lastActivity || Date.now();
        if (Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
          clearSession();
          setLoading(false);
          return;
        }

        // isTokenValid() above only decodes the payload client-side (checks
        // `exp`) — it never verifies the HMAC signature or that the admin
        // account is still active, so a hand-crafted localStorage entry with
        // a future `exp` could pass route guards without ever touching the
        // server. Confirm the session is real before trusting it. Progress
        // Tracker B6.
        const res = await fetch(`${getApiUrl()}/api.php?action=admin_verify_session`, {
          headers: { Authorization: `Bearer ${parsed.token}` },
        });
        const data = await res.json();
        if (!data.success || !data.admin) {
          clearSession();
          setLoading(false);
          return;
        }

        // Refresh role/name/email from the server in case they changed since
        // the token was issued; keep the client-tracked lastActivity/token.
        const verified: Admin = {
          ...parsed,
          id: Number(data.admin.id),
          name: data.admin.name,
          email: data.admin.email,
          role: data.admin.role as AdminRole,
        };
        setAdmin(verified);
        localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(verified));
        resetInactivityTimer();
      } catch {
        clearSession();
      }
      setLoading(false);
    };
    restore();
  }, []);

  useEffect(() => {
    if (!admin) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetInactivityTimer();
    events.forEach(evt => window.addEventListener(evt, handler, { passive: true }));
    return () => {
      events.forEach(evt => window.removeEventListener(evt, handler));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [admin, resetInactivityTimer]);

  useEffect(() => {
    if (!admin) return;
    const interval = setInterval(() => {
      if (!isTokenValid(admin.token)) {
        clearSession();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [admin, clearSession]);

  const signIn = async (email: string, password: string): Promise<{ error: Error | null }> => {
    const attempts = getLoginAttempts();
    if (attempts.lockUntil > Date.now()) {
      const minutesLeft = Math.ceil((attempts.lockUntil - Date.now()) / 60000);
      return { error: new Error(`Too many failed attempts. Try again in ${minutesLeft} minutes.`) };
    }

    if (!email || !password) {
      return { error: new Error('Email and password are required') };
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { error: new Error('Invalid email format') };
    }
    if (password.length < 6) {
      return { error: new Error('Password must be at least 6 characters') };
    }

    try {
      const formData = new FormData();
      formData.append('action', 'admin_login');
      formData.append('email', email.trim().toLowerCase());
      formData.append('password', password);

      const res = await fetch(`${getApiUrl()}/api.php`, { method: 'POST', body: formData });
      const data = await res.json();

      if (!data.success || !data.admin) {
        const currentAttempts = getLoginAttempts();
        const newCount = currentAttempts.count + 1;
        setLoginAttempts({
          count: newCount,
          lockUntil: newCount >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOCKOUT_DURATION_MS : 0,
        });
        return { error: new Error(data.message || 'Invalid credentials') };
      }

      const adminData: Admin = {
        id: Number(data.admin.id),
        name: data.admin.name,
        email: data.admin.email,
        role: data.admin.role as AdminRole,
        token: data.admin.token,
        lastActivity: Date.now(),
      };
      setAdmin(adminData);
      localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(adminData));
      resetLoginAttempts();
      resetInactivityTimer();
      return { error: null };
    } catch (err) {
      const currentAttempts = getLoginAttempts();
      const newCount = currentAttempts.count + 1;
      setLoginAttempts({
        count: newCount,
        lockUntil: newCount >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOCKOUT_DURATION_MS : 0,
      });
      return { error: err instanceof Error ? err : new Error('Login failed') };
    }
  };

  const signOut = useCallback(() => {
    clearSession();
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  }, [clearSession]);

  const isSuperAdmin = admin?.role === 'super_admin';
  const isAdmin = admin?.role === 'admin' || isSuperAdmin;
  const isModerator = admin?.role === 'moderator' || isAdmin;
  const isDataEntry = admin?.role === 'data_entry' || isModerator;

  return (
    <AdminAuthContext.Provider value={{
      admin,
      loading,
      signIn,
      signOut,
      isSuperAdmin,
      isAdmin,
      isModerator,
      isDataEntry,
      refreshActivity,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
